import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	failRemixCreateExperience,
	failRemixOpenMenuExperience,
	getActiveRemixCreateId,
} from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { expVal } from '@confluence/feature-experiments';

import type { ObjectSidebarPageRemixStartRejectionReason } from './ObjectSidebarPageRemixStart.ts';

type RejectedAttempt = {
	createId: string | undefined;
	isCurrent: () => boolean;
	onFailure?: () => void;
};

export const PAGE_REMIX_START_ERROR_FLAG_ID = 'page-remix-start-error';

const messages = defineMessages({
	title: {
		id: 'malleable-ui-common.page-remix.start-error.title',
		defaultMessage: 'We couldn’t start Remix',
		description: 'Title of the error flag when page Remix cannot prepare the page content',
	},
	empty: {
		id: 'malleable-ui-common.page-remix.start-error.empty',
		defaultMessage: 'Add content to this page, then try again.',
		description: 'Recovery instruction when page Remix finds no usable page content',
	},
	technical: {
		id: 'malleable-ui-common.page-remix.start-error.technical',
		defaultMessage: 'Refresh the page and try again.',
		description: 'Recovery instruction when page Remix cannot load or prepare page content',
	},
});

export const usePageRemixStartRejection = (
	flags: FlagsStateContainer,
): ((reason: ObjectSidebarPageRemixStartRejectionReason, attempt: RejectedAttempt) => boolean) => {
	const { formatMessage } = useIntl();
	const experienceTracker = useAIMateExperienceTracker();
	return useCallback(
		(reason: ObjectSidebarPageRemixStartRejectionReason, attempt: RejectedAttempt): boolean => {
			if (
				!['adf-load-failed', 'empty-adf', 'malformed-adf'].includes(reason) ||
				!attempt.isCurrent() ||
				attempt.createId === undefined ||
				getActiveRemixCreateId() !== attempt.createId ||
				!expVal('cc_maui_page_remix_broken_media_recovery', 'isEnabled', false)
			) {
				return false;
			}

			// The create helpers share a global context. Check ownership before settling it; settling
			// clears its ID, so the flag and local cleanup must use this same ownership decision.
			const error = new Error(`Page Remix preparation failed: ${reason}`);
			experienceTracker.fail({ name: ExperienceName.REMIX_START, teamName: 'CC CE MAU', error });
			failRemixOpenMenuExperience(experienceTracker, error);
			failRemixCreateExperience(experienceTracker, error);
			void flags.showErrorFlag({
				id: PAGE_REMIX_START_ERROR_FLAG_ID,
				title: formatMessage(messages.title),
				description: formatMessage(reason === 'empty-adf' ? messages.empty : messages.technical),
				isAutoDismiss: false,
			});
			attempt.onFailure?.();
			return true;
		},
		[experienceTracker, flags, formatMessage],
	);
};
